import { useAuth0 } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';

import { SHOW_TICKET_ALERT_STORAGE_KEY } from '@/constants/common';
import { axiosClient, handleApiError } from '@/services/BaseApi.service';
import { userAtom } from '@/store/user.store';
import type { LoginType } from '@/types/common';
import { getUserInitials } from '@/utils/Common.utils';

export const TOKEN_STORAGE_KEY = 'access_token';
export const REDIRECT_STORAGE_KEY = 'redirect_url';
export const LOGIN_URL = `${window.location.origin}/login`;
export const getAccessToken = () => localStorage.getItem(TOKEN_STORAGE_KEY);
export const authInitAtom = atom(false);
export const authIsAuthenticatedAtom = atom(false);
export const authLoadingUserAtom = atom(true);
// Use this helper to navigate to other routes outside of react components where use cannot use useNavigate
export const globalNavigate: { navigate: NavigateFunction } = {
  navigate: () => {
    // no-op until the router provides the real navigate function
  }
};

// Returns a function that checks the authentication of the user.
// By default is accesses the /auth/me endpoint which returns the cached user data.
// If the source is login it accesses the /auth/login endpoint and refreshes the cached user data.
// If there is an error store error in sessionStorage and logout the user. Error will be displayed on the login page
export const useFetchAuth = (source: 'me' | 'login' = 'me') => {
  const fetchMe = useCallback(
    () => axiosClient.get(`/auth/${source}`).catch(handleApiError),
    []
  );

  return fetchMe;
};

/**
 * The authentication consists of multiple parts:
 * 1. The user is authenticated by auth0
 * 2. The access token is fetched from auth0 asynchronously
 * 3. The user is verified by the backend via /api/me and access token
 * If all 3 steps are successful the user is authenticated and the user data is stored in the userAtom
 * If an access token is found in localStorage it will be first veified by the backend. Based on the result the user is authenticated or not
 */
export const useAuth = () => {
  globalNavigate.navigate = useNavigate();
  const login = useFetchAuth('login');
  const [init, setInit] = useAtom(authInitAtom);
  const [loadingUser, setLoadingUser] = useAtom(authLoadingUserAtom);
  const [userData, setUserData] = useAtom(userAtom);
  const [isAuthenticated, setIsAuthenticated] = useAtom(
    authIsAuthenticatedAtom
  );
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem(TOKEN_STORAGE_KEY)
  );

  const {
    user,
    isLoading: isAuth0Loading,
    isAuthenticated: isAuth0Authenticated,
    logout,
    getAccessTokenSilently
  } = useAuth0();

  // If the user is not authenticated by auth0 and the access token is not available the user is not authenticated
  useEffect(() => {
    if (!isAuth0Authenticated && !isAuth0Loading && getAccessToken() === null) {
      setLoadingUser(false);
      setIsAuthenticated(false);
      setInit(true);
    }
  }, [
    isAuth0Authenticated,
    isAuth0Loading,
    setLoadingUser,
    setIsAuthenticated,
    setInit
  ]);

  const { data, isSuccess, isError, error } = useQuery<LoginType>({
    queryKey: ['login'],
    queryFn: login as any, // TODO: Define login type
    staleTime: 1000 * 60, // 1 minute
    enabled: !init && accessToken !== null,
    retry: false
  });

  // Handle authentication errors
  useEffect(() => {
    if (isError) {
      console.error('Authentication error:', error);
      setLoadingUser(false);
      setIsAuthenticated(false);
      setInit(true);
    }
  }, [isError, error, setLoadingUser, setIsAuthenticated, setInit]);

  // When an access token is available fetch the user data from /api/me
  useEffect(() => {
    if (isSuccess && data?.data) {
      const userInitials = getUserInitials(user?.name || '');
      setIsAuthenticated(true);
      setLoadingUser(false);
      // combine user data from auth0 and /api/auth/me into one userInfo object
      setUserData({
        ...user,
        ...data.data,
        phone_number: data.data.phone_number || '',
        internal: !!data.data.internal,
        userInitials,
        image_url: user?.picture || data.data.image_url
      });
      datadogRum.setUser({
        name: user?.name,
        email: user?.email
      });
      setInit(true);
    }
  }, [
    isSuccess,
    data,
    user,
    setIsAuthenticated,
    setLoadingUser,
    setUserData,
    setInit
  ]);

  // Once user is authenticated by auth0 fetch the access token asynchronously and store in localStorage
  useEffect(() => {
    if (!isAuth0Authenticated || accessToken !== null) return;

    const fetchAccessToken = async () => {
      const token = await getAccessTokenSilently();
      if (!token) return;
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      setAccessToken(token);
    };

    fetchAccessToken();
  }, [
    isAuth0Authenticated,
    setAccessToken,
    getAccessTokenSilently,
    accessToken
  ]);

  // This logout method should be used everywhere in the app within react components
  const scopeLogout = useCallback(() => {
    axiosClient.get('/auth/logout').finally(() => {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(SHOW_TICKET_ALERT_STORAGE_KEY);
      logout({
        logoutParams: { returnTo: LOGIN_URL }
      });
    });
  }, [logout]);

  return {
    user: userData,
    isLoading: loadingUser,
    isAuthenticated,
    accessToken,
    logout: scopeLogout
  };
};

export default useAuth;
